import _, { kebabCase, sortBy } from 'lodash';
import { DateTime } from 'luxon';
import React from 'react';
import { useLocation, useParams } from 'react-router-dom';
import ContentContainer from '~/src/components/ContentContainer';
import Games from '~/src/components/Index/Games';
import Layout from '~/src/components/Layout';
import PageHeader from '~/src/components/PageHeader';
import Suspended from '~/src/components/Suspended';
import { Ot7Event } from '~/src/models/Ot7Event';
import useFetch from '~/src/overtime-lib/src/www/hooks/useFetch';
import { Urls } from '../../lib/enums/urls';
import { Ot7Game } from '../../models/Ot7Game';
import './Schedule.scss';

const Header = ({ ot7_events }: { ot7_events: Ot7Event[] }) => {
	const { pathname } = useLocation();
	const standingsUrl = pathname.replace('/schedule', '/standings');

	return (
		<PageHeader
			tabs={[['all', 'All'], ...ot7_events.map((e) => [kebabCase(e.slug), e.title])]}
			tabRoot={'/schedule'}
			ctaText={'Standings'}
			ctaUrl={'/standings'}
		>
			Schedule
		</PageHeader>
	);
};

const Schedule = () => {
	let { section: currentTab } = useParams();

	const fetchEvents = useFetch<Ot7Event[]>({
		url: `${Urls.RestApiBaseUrl}/ot7_events/v1/public`,
		key: 'ot7_events',
	});
	const events = sortBy(fetchEvents?.result.read(), 'starts_at');

	const fetchGames = useFetch<Ot7Game[]>({
		url: `${Urls.RestApiBaseUrl}/ot7_games/v1/public?all=true`,
		key: 'ot7_games',
	});
	const games = sortBy(fetchGames?.result.read(), 'starts_at');

	// get all events with games in them
	const eventsWithGames = events.filter((e) => games.find((g) => g.ot7_event_id === e.id));

	// get events with games in the future, but include events within the past few days
	// so basically show scores for the past few days and then switch to current week as next event get closer
	const futureEventsWithGames = eventsWithGames.filter(
		(e) => DateTime.fromISO(e.starts_at).plus({ days: 4 }) >= DateTime.now(),
	);

	// get the next event
	const nextEventWithGames = _.orderBy(futureEventsWithGames, 'starts_at', 'asc').at(0);

	// get the last event
	const lastEventWithGames = eventsWithGames.at(-1);

	// if no tab is selected, redirect user to the current week
	if (!currentTab) {
		if (!nextEventWithGames && lastEventWithGames) {
			// If no future/next event, then redirect to last event (with or without games)
			currentTab = lastEventWithGames.slug;
		} else if (nextEventWithGames) {
			// Otherwise redirect to the next event
			currentTab = nextEventWithGames.slug;
		} else {
			// Default to the all tab
			currentTab = 'all';
		}
	}

	return (
		<Layout>
			<div className="Schedule">
				<Suspended>
					<Header ot7_events={eventsWithGames} />
				</Suspended>
				<ContentContainer>
					<Suspended>
						<Games showAll={true} multiColumn={false} event={currentTab} />
					</Suspended>
				</ContentContainer>
			</div>
		</Layout>
	);
};

export default Schedule;
